import React from 'react';
import { Lead } from '../types';
import EditableCell from './EditableCell';

interface LeadTableProps {
  leads: Lead[];
  loading: boolean;
  error: string | null;
  onUpdateLead: (leadId: string, field: keyof Lead, value: string) => Promise<void>;
}

/**
 * LeadTable component with inline editing capabilities
 */
const LeadTable: React.FC<LeadTableProps> = ({ 
  leads, 
  loading, 
  error, 
  onUpdateLead 
}) => {
  
  // Available status options for dropdown
  const statusOptions = ['new', 'contacted', 'qualified', 'converted'];
  
  /**
   * Format date string to human-readable format
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  /**
   * Get CSS class for status badge
   */
  const getStatusClass = (status: string): string => {
    return `status-badge status-${status.toLowerCase()}`;
  };

  /**
   * Handle field updates for a specific lead
   */
  const handleUpdateField = (leadId: string, field: keyof Lead) => {
    return async (newValue: string) => {
      await onUpdateLead(leadId, field, newValue);
    };
  };

  // Show loading state
  if (loading) {
    return (
      <div className="lead-table-container">
        <div className="loading">Loading leads...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="lead-table-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  // Show empty state
  if (leads.length === 0) {
    return (
      <div className="lead-table-container">
        <div className="no-data">No leads found</div>
      </div>
    );
  }

  return (
    <div className="lead-table-container">
      <table className="lead-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.id}>
              <td>
                <EditableCell
                  value={lead.name}
                  onSave={handleUpdateField(lead.id, 'name')}
                  type="text"
                  placeholder="Enter name"
                />
              </td>
              <td>
                <EditableCell
                  value={lead.email}
                  onSave={handleUpdateField(lead.id, 'email')}
                  type="email"
                  placeholder="Enter email"
                />
              </td>
              <td>
                <div className={getStatusClass(lead.status)}>
                  <EditableCell
                    value={lead.status}
                    onSave={handleUpdateField(lead.id, 'status')}
                    type="select"
                    options={statusOptions}
                  />
                </div>
              </td>
              <td className="non-editable">
                {formatDate(lead.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LeadTable;